import React, { useEffect, useRef, useState } from 'react';

import {
  Content,
  Header,
  HeaderLabel,
  Page,
  SupportButton,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  IdentityApi,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';

import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/Check';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import LockIcon from '@material-ui/icons/Lock';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import SecurityIcon from '@material-ui/icons/Security';
import ShareIcon from '@material-ui/icons/Share';

const useStyles = makeStyles(theme => {
  const isDark = theme.palette.type === 'dark';

  const wrapColor = '#2e7d32';
  const wrapColorLight = isDark
    ? 'rgba(46,125,50,0.15)'
    : 'rgba(46,125,50,0.08)';
  const wrapColorBorder = isDark
    ? 'rgba(46,125,50,0.40)'
    : 'rgba(46,125,50,0.28)';

  const unwrapColor = '#c62828';
  const unwrapColorLight = isDark
    ? 'rgba(198,40,40,0.15)'
    : 'rgba(198,40,40,0.08)';
  const unwrapColorBorder = isDark
    ? 'rgba(198,40,40,0.40)'
    : 'rgba(198,40,40,0.28)';

  const errorFg = theme.palette.error.main;
  const errorBg = isDark ? 'rgba(211,47,47,0.10)' : 'rgba(211,47,47,0.07)';
  const errorBorder = isDark ? 'rgba(211,47,47,0.35)' : 'rgba(211,47,47,0.22)';

  const warnFg = isDark ? '#f9a825' : '#e65100';
  const warnBg = isDark ? 'rgba(249,168,37,0.10)' : 'rgba(230,81,0,0.06)';
  const warnBorder = isDark ? 'rgba(249,168,37,0.28)' : 'rgba(230,81,0,0.20)';

  return {
    content: {
      paddingTop: theme.spacing(3),
    },

    shellCard: {
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius * 2,
      overflow: 'hidden',
    },

    titleBar: {
      background: isDark ? theme.palette.grey[900] : theme.palette.grey[100],
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 2),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    termDots: {
      display: 'flex',
      gap: 5,
    },
    dot: {
      width: 11,
      height: 11,
      borderRadius: '50%',
    },
    dotRed: { background: '#ff5f57' },
    dotYellow: { background: '#febc2e' },
    dotGreen: { background: '#28c840' },
    titleText: {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
      flex: 1,
      textAlign: 'center' as const,
    },
    panelsWrapper: {
      display: 'flex',
      flexDirection: 'column' as const,
    },

    panel: {
      padding: theme.spacing(3),
    },
    panelLeft: {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },

    panelHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(2.5),
    },
    panelIconWrap: {
      width: 32,
      height: 32,
      borderRadius: theme.shape.borderRadius,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    panelIconWrapEncrypt: {
      background: wrapColorLight,
      border: `1px solid ${wrapColorBorder}`,
      color: wrapColor,
    },
    panelIconWrapDecrypt: {
      background: unwrapColorLight,
      border: `1px solid ${unwrapColorBorder}`,
      color: unwrapColor,
    },
    panelTitle: {
      fontSize: '0.9rem',
      fontWeight: 700,
      color: theme.palette.text.primary,
      lineHeight: 1.3,
    },
    panelSubtitle: {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
    },

    inputLabel: {
      fontSize: '0.72rem',
      fontWeight: 600,
      color: theme.palette.text.secondary,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.07em',
      marginBottom: theme.spacing(0.75),
    },

    inputField: {
      '& .MuiOutlinedInput-root': {
        fontSize: '0.85rem',
        background: isDark ? 'rgba(0,0,0,0.18)' : theme.palette.grey[50],
        borderRadius: theme.shape.borderRadius,
        color: theme.palette.text.primary,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.divider,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
          borderWidth: 1,
        },
      },
      '& textarea': {
        color: theme.palette.text.primary,
      },
    },
    inputFieldEncrypt: {
      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: `${wrapColor} !important`,
      },
    },
    inputFieldDecrypt: {
      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: `${unwrapColor} !important`,
      },
    },

    submitBtn: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(0.9, 2.5),
      borderRadius: theme.shape.borderRadius,
      fontWeight: 700,
      fontSize: '0.82rem',
      textTransform: 'none' as const,
      boxShadow: 'none',
    },
    submitEncrypt: {
      background: `${wrapColor} !important`,
      color: '#fff !important',
      '&:hover': {
        background: `${wrapColor} !important`,
        filter: 'brightness(1.1)',
        boxShadow: `0 2px 8px ${wrapColorBorder} !important`,
      },
      '&.Mui-disabled': { opacity: '0.4 !important' },
    },
    submitDecrypt: {
      background: `${unwrapColor} !important`,
      color: '#fff !important',
      '&:hover': {
        background: `${unwrapColor} !important`,
        filter: 'brightness(1.1)',
        boxShadow: `0 2px 8px ${unwrapColorBorder} !important`,
      },
      '&.Mui-disabled': { opacity: '0.4 !important' },
    },

    outputDivider: {
      margin: theme.spacing(2.5, 0, 2),
    },
    outputRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.25),
      flexWrap: 'wrap' as const,
    },
    outputLabel: {
      fontSize: '0.68rem',
      fontWeight: 700,
      letterSpacing: '0.1em',
      textTransform: 'uppercase' as const,
      color: theme.palette.text.secondary,
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      padding: '2px 8px',
      borderRadius: theme.shape.borderRadius,
      fontSize: '0.67rem',
      fontWeight: 700,
      lineHeight: '18px',
    },
    badgeEncrypt: {
      background: wrapColorLight,
      color: wrapColor,
      border: `1px solid ${wrapColorBorder}`,
    },
    badgeDecrypt: {
      background: unwrapColorLight,
      color: unwrapColor,
      border: `1px solid ${unwrapColorBorder}`,
    },
    badgeError: {
      background: errorBg,
      color: errorFg,
      border: `1px solid ${errorBorder}`,
    },
    badgeMeta: {
      fontSize: '0.68rem',
      color: theme.palette.text.secondary,
    },

    resultFieldEncrypt: {
      '& .MuiOutlinedInput-root': {
        fontSize: '0.82rem',
        background: wrapColorLight,
        borderRadius: theme.shape.borderRadius,
        '& .MuiOutlinedInput-notchedOutline': { borderColor: wrapColorBorder },
        '& textarea': { color: wrapColor },
      },
    },
    resultFieldDecrypt: {
      '& .MuiOutlinedInput-root': {
        fontSize: '0.82rem',
        background: unwrapColorLight,
        borderRadius: theme.shape.borderRadius,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: unwrapColorBorder,
        },
        '& textarea': { color: unwrapColor },
      },
    },

    copyBtn: {
      color: theme.palette.text.secondary,
      padding: 4,
      '&:hover': {
        color: theme.palette.text.primary,
        background: theme.palette.action.hover,
      },
    },
    actionRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginTop: theme.spacing(1.5),
      flexWrap: 'wrap' as const,
    },
    actionBtn: {
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'none' as const,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(0.5, 1.5),
    },
    actionBtnShare: {
      borderColor: `${wrapColor}80 !important`,
      color: `${wrapColor} !important`,
      '&:hover': {
        borderColor: `${wrapColor} !important`,
        background: `${wrapColorLight} !important`,
      },
    },

    errorBox: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
      padding: theme.spacing(1.5),
      borderRadius: theme.shape.borderRadius,
      background: errorBg,
      border: `1px solid ${errorBorder}`,
    },
    errorIcon: {
      color: `${errorFg} !important`,
      fontSize: '1rem !important',
      marginTop: 1,
      flexShrink: 0,
    },
    errorText: {
      color: errorFg,
      fontSize: '0.82rem',
      lineHeight: 1.6,
    },

    card: {
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius * 2,
      overflow: 'hidden',
    },
    cardHeader: {
      padding: theme.spacing(1.75, 2.25),
      borderBottom: `1px solid ${theme.palette.divider}`,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.25),
    },
    cardIconWrap: {
      width: 30,
      height: 30,
      borderRadius: theme.shape.borderRadius,
      background: isDark
        ? `${theme.palette.primary.main}22`
        : `${theme.palette.primary.main}15`,
      border: `1px solid ${
        isDark
          ? `${theme.palette.primary.main}35`
          : `${theme.palette.primary.main}28`
      }`,
      color: theme.palette.primary.main,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    cardTitle: {
      fontSize: '0.84rem',
      fontWeight: 700,
      color: theme.palette.text.primary,
    },
    cardBody: {
      padding: theme.spacing(2.25),
    },
    stepList: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: theme.spacing(2),
    },
    stepRow: {
      display: 'flex',
      gap: theme.spacing(1.5),
      alignItems: 'flex-start',
    },
    stepNum: {
      minWidth: 22,
      height: 22,
      borderRadius: '50%',
      background: isDark
        ? `${theme.palette.primary.main}22`
        : `${theme.palette.primary.main}15`,
      border: `1px solid ${
        isDark
          ? `${theme.palette.primary.main}35`
          : `${theme.palette.primary.main}28`
      }`,
      color: theme.palette.primary.main,
      fontSize: '0.65rem',
      fontWeight: 800,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      marginTop: 1,
    },
    stepTitle: {
      fontSize: '0.80rem',
      fontWeight: 700,
      color: theme.palette.text.primary,
      marginBottom: 2,
    },
    stepDesc: {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
      lineHeight: 1.6,
      '& strong': { color: theme.palette.text.primary, fontWeight: 600 },
    },
    secList: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
    },
    secItem: {
      display: 'flex',
      gap: theme.spacing(1.25),
      alignItems: 'flex-start',
      padding: theme.spacing(1, 1.25),
      borderRadius: theme.shape.borderRadius,
      background: warnBg,
      border: `1px solid ${warnBorder}`,
    },
    secItemIcon: {
      color: `${warnFg} !important`,
      fontSize: '0.9rem !important',
      marginTop: 2,
      flexShrink: 0,
    },
    secItemLabel: {
      fontSize: '0.73rem',
      fontWeight: 700,
      color: warnFg,
      display: 'block',
      marginBottom: 1,
    },
    secItemText: {
      fontSize: '0.72rem',
      color: theme.palette.text.secondary,
      lineHeight: 1.55,
    },
    secFootnote: {
      padding: theme.spacing(1.1, 1.4),
      borderRadius: theme.shape.borderRadius,
      background: theme.palette.action.hover,
      border: `1px solid ${theme.palette.divider}`,
    },
    secFootnoteText: {
      fontSize: '0.72rem',
      color: theme.palette.text.secondary,
      lineHeight: 1.6,
      '& strong': { color: theme.palette.text.primary, fontWeight: 600 },
    },
  };
});

async function fetchData(
  endpoint: string,
  payload: string,
  identityApi: IdentityApi,
): Promise<string> {
  const { token } = await identityApi.getCredentials();
  const response = await fetch(endpoint, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify({ payload }),
  });
  if (!response.ok) {
    throw new Error(
      `Request failed — ${response.status} ${response.statusText}`,
    );
  }
  return response.text();
}

interface SidePanelCardProps {
  classes: ReturnType<typeof useStyles>;
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

function SidePanelCard({ classes, icon, title, children }: SidePanelCardProps) {
  return (
    <Box className={classes.card}>
      <Box className={classes.cardHeader}>
        <Box className={classes.cardIconWrap}>{icon}</Box>
        <Typography className={classes.cardTitle}>{title}</Typography>
      </Box>
      <Box className={classes.cardBody}>{children}</Box>
    </Box>
  );
}

interface PanelProps {
  mode: 'wrap' | 'unwrap';
  classes: ReturnType<typeof useStyles>;
  identityApi: IdentityApi;
  discoveryApi: { getBaseUrl: (id: string) => Promise<string> };
  panelClass?: string;
}

function SecretPanel({
  mode,
  classes,
  identityApi,
  discoveryApi,
  panelClass,
}: PanelProps) {
  const [inputText, setInputText] = useState('');
  const [output, setOutput] = useState<{ value: string; error?: string }>({
    value: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [msgCopied, setMsgCopied] = useState(false);

  const tokenTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const msgTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (tokenTimer.current) clearTimeout(tokenTimer.current);
      if (msgTimer.current) clearTimeout(msgTimer.current);
    },
    [],
  );

  const handleSubmit = async () => {
    setIsLoading(true);
    setOutput({ value: '' });
    try {
      const base = await discoveryApi.getBaseUrl('secrets-sharing');
      const endpoint =
        mode === 'wrap' ? `${base}/hcpVault/wrap` : `${base}/hcpVault/unwrap`;
      const result = await fetchData(endpoint, inputText, identityApi);
      setOutput({ value: result });
    } catch (e: unknown) {
      setOutput({ value: '', error: (e as Error).message });
    } finally {
      setIsLoading(false);
    }
  };

  const copyToken = () => {
    navigator.clipboard.writeText(output.value);
    setTokenCopied(true);
    if (tokenTimer.current) clearTimeout(tokenTimer.current);
    tokenTimer.current = setTimeout(() => setTokenCopied(false), 2000);
  };

  const copyMessage = () => {
    const message =
      `I'm sharing sensitive information with you securely.\n\n` +
      `To access it:\n` +
      `1. Go to: ${window.location.href}\n` +
      `2. Paste the token below and click "Unwrap & Reveal"\n\n` +
      `⚠️  This token is single-use and expires in 7 days.\n\n` +
      `Token:\n${output.value}`;
    navigator.clipboard.writeText(message);
    setMsgCopied(true);
    if (msgTimer.current) clearTimeout(msgTimer.current);
    msgTimer.current = setTimeout(() => setMsgCopied(false), 2000);
  };

  const hasResult = Boolean(output.value);
  const hasError = Boolean(output.error);
  const canSubmit = !isLoading && Boolean(inputText.trim());
  const isWrap = mode === 'wrap';

  let submitIcon: React.ReactNode;
  let submitLabel: string;

  if (isLoading) {
    submitIcon = <CircularProgress size={13} color="inherit" />;
    submitLabel = isWrap ? 'Encrypting…' : 'Decrypting…';
  } else if (isWrap) {
    submitIcon = <LockIcon style={{ fontSize: 14 }} />;
    submitLabel = 'Wrap Secret';
  } else {
    submitIcon = <LockOpenIcon style={{ fontSize: 14 }} />;
    submitLabel = 'Unwrap & Reveal';
  }

  return (
    <Box className={`${panelClass ?? ''} ${classes.panel}`}>
      <Box className={classes.panelHeader}>
        <Box
          className={`${classes.panelIconWrap} ${
            isWrap ? classes.panelIconWrapEncrypt : classes.panelIconWrapDecrypt
          }`}
        >
          {isWrap ? (
            <LockIcon style={{ fontSize: 16 }} />
          ) : (
            <LockOpenIcon style={{ fontSize: 16 }} />
          )}
        </Box>
        <Box>
          <Typography className={classes.panelTitle}>
            {isWrap ? 'Wrap Secret' : 'Unwrap Secret'}
          </Typography>
          <Typography className={classes.panelSubtitle}>
            {isWrap
              ? 'Encrypt payload → one-time token'
              : 'Token → reveal secret'}
          </Typography>
        </Box>
      </Box>

      <Typography className={classes.inputLabel}>
        {isWrap ? 'Sensitive Payload' : 'Wrapped Token'}
      </Typography>

      <TextField
        variant="outlined"
        fullWidth
        multiline
        minRows={isWrap ? 5 : 3}
        value={inputText}
        onChange={e => setInputText(e.target.value)}
        placeholder={
          isWrap
            ? 'Paste credentials, API keys, tokens…'
            : 'Paste the wrapped token…'
        }
        className={`${classes.inputField} ${
          isWrap ? classes.inputFieldEncrypt : classes.inputFieldDecrypt
        }`}
        inputProps={{
          'aria-label': isWrap ? 'wrap secret input' : 'unwrap secret input',
        }}
      />

      <Button
        variant="contained"
        disableElevation
        className={`${classes.submitBtn} ${
          isWrap ? classes.submitEncrypt : classes.submitDecrypt
        }`}
        onClick={handleSubmit}
        disabled={!canSubmit}
        startIcon={submitIcon}
      >
        {submitLabel}
      </Button>

      <Collapse in={hasResult || hasError}>
        <Divider className={classes.outputDivider} />

        <Box className={classes.outputRow}>
          <Typography className={classes.outputLabel}>Output</Typography>

          {hasResult && (
            <Box
              component="span"
              className={`${classes.badge} ${
                isWrap ? classes.badgeEncrypt : classes.badgeDecrypt
              }`}
            >
              <CheckCircleOutlineIcon style={{ fontSize: 10 }} />
              {isWrap ? 'Wrapped' : 'Unwrapped'}
            </Box>
          )}

          {hasError && (
            <Box
              component="span"
              className={`${classes.badge} ${classes.badgeError}`}
            >
              <ErrorOutlineIcon style={{ fontSize: 10 }} />
              Error
            </Box>
          )}

          {hasResult && (
            <Typography className={classes.badgeMeta}>
              {isWrap ? '— ttl=7d · single-use' : '— token destroyed'}
            </Typography>
          )}
        </Box>

        {hasResult && (
          <>
            <TextField
              variant="outlined"
              fullWidth
              multiline
              value={output.value}
              className={
                isWrap ? classes.resultFieldEncrypt : classes.resultFieldDecrypt
              }
              inputProps={{ readOnly: true, 'aria-label': 'result output' }}
              InputProps={{
                endAdornment: (
                  <Tooltip
                    title={tokenCopied ? 'Copied!' : 'Copy to clipboard'}
                  >
                    <IconButton
                      size="small"
                      onClick={copyToken}
                      className={classes.copyBtn}
                    >
                      {tokenCopied ? (
                        <CheckIcon style={{ fontSize: 13 }} />
                      ) : (
                        <FileCopyIcon style={{ fontSize: 13 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                ),
              }}
            />

            <Box className={classes.actionRow}>
              <Button
                variant="outlined"
                size="small"
                className={classes.actionBtn}
                startIcon={
                  tokenCopied ? (
                    <CheckIcon style={{ fontSize: 11 }} />
                  ) : (
                    <FileCopyIcon style={{ fontSize: 11 }} />
                  )
                }
                onClick={copyToken}
              >
                {tokenCopied ? 'Copied!' : 'Copy Token'}
              </Button>

              {isWrap && (
                <Button
                  variant="outlined"
                  size="small"
                  className={`${classes.actionBtn} ${classes.actionBtnShare}`}
                  startIcon={
                    msgCopied ? (
                      <CheckIcon style={{ fontSize: 11 }} />
                    ) : (
                      <ShareIcon style={{ fontSize: 11 }} />
                    )
                  }
                  onClick={copyMessage}
                >
                  {msgCopied ? 'Message Copied!' : 'Copy Sharing Message'}
                </Button>
              )}
            </Box>
          </>
        )}

        {hasError && (
          <Box className={classes.errorBox}>
            <ErrorOutlineIcon className={classes.errorIcon} />
            <Typography className={classes.errorText}>
              {output.error}
            </Typography>
          </Box>
        )}
      </Collapse>
    </Box>
  );
}

export function SecretsSharing() {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  return (
    <Page themeId="tool">
      <Header
        title="Secrets Sharing"
        subtitle="Encrypt sensitive payloads into single-use, time-limited tokens"
      >
        <HeaderLabel label="Backend" value="HCP Vault" />
        <HeaderLabel label="Token TTL" value="7 days" />
        <SupportButton>
          Use <strong>Wrap</strong> to encrypt any sensitive payload into a
          one-time token. Share the token via Slack, Email, or Teams. The
          recipient pastes it here and presses{' '}
          <strong>Unwrap &amp; Reveal</strong>. Tokens are single-use and expire
          after 7 days.
        </SupportButton>
      </Header>

      <Content className={classes.content}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Box className={classes.shellCard}>
              <Box className={classes.titleBar}>
                <Box className={classes.termDots}>
                  <span className={`${classes.dot} ${classes.dotRed}`} />
                  <span className={`${classes.dot} ${classes.dotYellow}`} />
                  <span className={`${classes.dot} ${classes.dotGreen}`} />
                </Box>
                <Typography className={classes.titleText}>
                  secure-secrets-sharing
                </Typography>
              </Box>

              <Box className={classes.panelsWrapper}>
                <SecretPanel
                  mode="wrap"
                  classes={classes}
                  identityApi={identityApi}
                  discoveryApi={discoveryApi}
                  panelClass={classes.panelLeft}
                />
                <SecretPanel
                  mode="unwrap"
                  classes={classes}
                  identityApi={identityApi}
                  discoveryApi={discoveryApi}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <SidePanelCard
                classes={classes}
                icon={<CheckCircleOutlineIcon style={{ fontSize: 16 }} />}
                title="How It Works"
              >
                <Box className={classes.stepList}>
                  {[
                    {
                      n: 1,
                      title: 'Wrap your payload',
                      desc: (
                        <>
                          Paste credentials, API keys, or any sensitive text and
                          click <strong>Wrap Secret</strong>. The payload is
                          encrypted in HCP Vault and you receive a single-use
                          token.
                        </>
                      ),
                    },
                    {
                      n: 2,
                      title: 'Share the token',
                      desc: (
                        <>
                          Copy just the token, or use{' '}
                          <strong>Copy Sharing Message</strong> to send a
                          pre-written message with full instructions.
                        </>
                      ),
                    },
                    {
                      n: 3,
                      title: 'Recipient unwraps',
                      desc: (
                        <>
                          Paste the token into the <strong>Unwrap</strong> panel
                          and click <strong>Unwrap &amp; Reveal</strong>. The
                          secret is shown once, then the token is destroyed.
                        </>
                      ),
                    },
                  ].map(step => (
                    <Box key={step.n} className={classes.stepRow}>
                      <span className={classes.stepNum}>{step.n}</span>
                      <Box>
                        <Typography className={classes.stepTitle}>
                          {step.title}
                        </Typography>
                        <Typography className={classes.stepDesc}>
                          {step.desc}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </SidePanelCard>

              <SidePanelCard
                classes={classes}
                icon={<SecurityIcon style={{ fontSize: 16 }} />}
                title="Security Notes"
              >
                <Box className={classes.secList}>
                  {[
                    {
                      label: 'Single-use only',
                      text: 'Each token can be unwrapped exactly once. Reusing a consumed token will fail.',
                    },
                    {
                      label: 'Expires after 7 days',
                      text: 'Tokens become invalid after 7 days regardless of whether they have been used.',
                    },
                    {
                      label: 'No recovery possible',
                      text: 'Once consumed or expired, the payload cannot be retrieved. Wrap again if needed.',
                    },
                  ].map(item => (
                    <Box key={item.label} className={classes.secItem}>
                      <ErrorOutlineIcon className={classes.secItemIcon} />
                      <Box>
                        <Typography
                          component="span"
                          className={classes.secItemLabel}
                        >
                          {item.label}
                        </Typography>
                        <Typography className={classes.secItemText}>
                          {item.text}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Box className={classes.secFootnote}>
                  <Typography className={classes.secFootnoteText}>
                    Never paste tokens directly into chat history or shared
                    docs. Use <strong>Copy Sharing Message</strong> to bundle
                    instructions and the token in one clearly labelled block.
                  </Typography>
                </Box>
              </SidePanelCard>
            </Box>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
}

export default SecretsSharing;
